import type {UseMutationOptions} from '@tanstack/react-query';
import {deleteInvenLike} from '../api/inven/deleteInvenLike';
import {deleteInvenPaid} from '../api/inven/deleteInvenPaid';
import {deleteInvenRecent} from '../api/inven/deleteInvenRecent';
import {postInvenOpenPreview} from '../api/inven/postInvenOpenPreview';
import {PagewebError} from '../errors/PagewebError.class';
import {
  DeleteInvenLikeMutation,
  type DeleteInvenLikeMutationVariables,
  DeleteInvenPaidMutation,
  type DeleteInvenPaidMutationVariables,
  DeleteInvenRecentMutation,
  type DeleteInvenRecentMutationVariables,
  OpenPreviewMutation,
  type OpenPreviewMutationVariables,
  ResultStatus,
} from '../graphql/generated/types';

/**
 * Inven 도메인 Mutation Adapter
 */
export const invenMutationAdapter = {
  /**
   * 찜 목록 삭제
   *
   * ⚠️ Resolver 로직:
   * - seriesIdList를 join(',')으로 문자열 변환
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공하면 {status: ResultStatus.Success} 반환
   * - React Query의 error state 사용
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(invenMutationAdapter.deleteInvenLike());
   * mutation.mutate({seriesIdList: [123, 456]});
   * // 성공: mutation.isSuccess
   * // 실패: mutation.isError
   * ```
   */
  deleteInvenLike: (): UseMutationOptions<
    DeleteInvenLikeMutation,
    PagewebError,
    DeleteInvenLikeMutationVariables
  > => ({
    mutationFn: async (variables: DeleteInvenLikeMutationVariables) => {
      const seriesUids = Array.isArray(variables.seriesIdList)
        ? variables.seriesIdList.join(',')
        : variables.seriesIdList;
      await deleteInvenLike(seriesUids);
      return {
        deleteInvenLike: {
          status: ResultStatus.Success,
        },
      };
    },
  }),

  /**
   * 유료 구매 목록 삭제
   *
   * ⚠️ Resolver 로직:
   * - seriesIdList를 join(',')으로 문자열 변환
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공하면 {status: ResultStatus.Success} 반환
   * - React Query의 error state 사용
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(invenMutationAdapter.deleteInvenPaid());
   * mutation.mutate({seriesIdList: [123, 456]});
   * // 성공: mutation.isSuccess
   * // 실패: mutation.isError
   * ```
   */
  deleteInvenPaid: (): UseMutationOptions<
    DeleteInvenPaidMutation,
    PagewebError,
    DeleteInvenPaidMutationVariables
  > => ({
    mutationFn: async (variables: DeleteInvenPaidMutationVariables) => {
      const seriesUids = Array.isArray(variables.seriesIdList)
        ? variables.seriesIdList.join(',')
        : variables.seriesIdList;
      await deleteInvenPaid(seriesUids);
      return {
        deleteInvenPaid: {
          status: ResultStatus.Success,
        },
      };
    },
  }),

  /**
   * 최근 본 목록 삭제
   *
   * ⚠️ Resolver 로직:
   * - seriesIdList를 join(',')으로 문자열 변환
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공하면 {status: ResultStatus.Success} 반환
   * - React Query의 error state 사용
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(invenMutationAdapter.deleteInvenRecent());
   * mutation.mutate({seriesIdList: [123, 456]});
   * // 성공: mutation.isSuccess
   * // 실패: mutation.isError
   * ```
   */
  deleteInvenRecent: (): UseMutationOptions<
    DeleteInvenRecentMutation,
    PagewebError,
    DeleteInvenRecentMutationVariables
  > => ({
    mutationFn: async (variables: DeleteInvenRecentMutationVariables) => {
      const seriesUids = Array.isArray(variables.seriesIdList)
        ? variables.seriesIdList.join(',')
        : variables.seriesIdList;
      await deleteInvenRecent(seriesUids);
      return {
        deleteInvenRecent: {
          status: ResultStatus.Success,
        },
      };
    },
  }),

  /**
   * 미리보기 열람
   *
   * ⚠️ Resolver 로직:
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공하면 ResultStatus.Success 반환
   * - React Query의 error state 사용
   * - transactionId는 requestContext.requestInfo.deviceId 사용
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(invenMutationAdapter.openPreview());
   * mutation.mutate({input: {seriesId: 123, productId: 456}});
   * // 성공: mutation.isSuccess
   * // 실패: mutation.isError
   * ```
   */
  openPreview: (): UseMutationOptions<
    OpenPreviewMutation,
    PagewebError,
    OpenPreviewMutationVariables
  > => ({
    mutationFn: async (variables: OpenPreviewMutationVariables) => {
      await postInvenOpenPreview({
        seriesId: Number(variables.input.seriesId),
        productId: Number(variables.input.productId),
      });
      return {openPreview: ResultStatus.Success};
    },
  }),
} as const;
